import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { useHandleErrors } from 'core/hooks';
import { submitOrderLogService } from 'dashboard/services';
import { ISubmitOrderLogReport, ISubmitOrderLogReportRequestPayload } from 'dashboard/types';

// The report is an unpaginated snapshot; without staleTime, every remount of the
// page refetches it and flashes the grid's loading overlay over correct data.
const STALE_TIME_MS = 5 * 60 * 1000;

export const useGetSubmitOrderLogReport = (
  requestPayload: ISubmitOrderLogReportRequestPayload,
  options: { enabled?: boolean } = {}
) => {
  const { enabled = true } = options;

  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    ['submitOrderLogReport', requestPayload],
    ({ signal }) => submitOrderLogService.getSubmitOrderLogReport(requestPayload, signal),
    {
      enabled,
      keepPreviousData: true,
      staleTime: STALE_TIME_MS,
    }
  );

  return {
    isError,
    isFetching,
    isLoading,
    refetch,
    report: data?.record as ISubmitOrderLogReport,
  };
};

// Imperative on-demand fetch for the copy action (a mutation so useHandleErrors
// surfaces failures as a notification instead of a silent console line).
export const useGetSubmitOrderLogEntry = () => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    error: { title: t('errorTitlePayloadCopy') },
  });

  const { mutateAsync, isLoading } = useMutation(async (id: number) => {
    const entry = await submitOrderLogService.getSubmitOrderLogEntry(id);

    // A success state over an empty clipboard would send the admin looking in
    // the wrong place — treat an uncaptured payload as a failure.
    if (entry?.requestBody == null) {
      throw new Error(`No request payload captured for entry ${id}`);
    }

    return entry;
  }, errors);

  return { getSubmitOrderLogEntry: mutateAsync, isLoading };
};

// Panel-side read of one entry. A query rather than the copy mutation: reopening
// the same row is served from cache, and "no payload captured" can be rendered in
// place instead of only surfacing as a notification.
export const useGetSubmitOrderLogEntryDetail = (id: number | null) => {
  const { data, isLoading, isError, refetch } = useQuery(
    ['submitOrderLogEntry', id],
    ({ signal }) => submitOrderLogService.getSubmitOrderLogEntry(id, signal),
    {
      enabled: null !== id,
      staleTime: STALE_TIME_MS,
    }
  );

  return { detail: data, isError, isLoading, refetch };
};
